import { transparentize } from 'polished';
import { keyframes } from 'styled-components';

/** @deprecated use ModernScripts COLOR instead */
export const COLOR = {
    // primary colours
    PRIMARY: {
        RED: '#e63327',
        GREEN: '#0dd32c',
        BLUE: '#0000ff',
        YELLOW: '#ffee00',
        BLACK: '#000',
        WHITE: '#fff',
        GREY: '#555',
        HOT_PINK: '#ff00be',
        HOT_PURPLE: '#CB35FC',
    },

    // main colours
    MAIN: {
        LIGHT_GREY: '#f7f9fb',
        MEDIUM_GREY: '#e4e8ed',
        DARK_GREY: '#51688d',
        DARK_BLUE: '#0b2a51',

        PRIMARY: '#1a2952',
        SECONDARY: '#00B1FF',
        ICON_BACKGROUND: '#F5F6F7',

        LABEL_TEXT: '#2A3F5E',
        FORM_FIELD: '#EDEDED',
        FORM_FIELD_DARK: 'rgb(130, 146, 173)',

        BUTTON_GROUP_BG: '#f8f9fa',
        BUTTON_GROUP_BORDER: '#e8e8e8',
    },

    // brand colours
    BRAND: {
        REPORTING: '#1ba3e7',
        FORECASTING: '#39b873',
        DASHBOARDS: '#e8ab36',
        MULTI: '#b65bd6',
    },

    // modal
    MODAL: {
        MODAL_PANEL_ACCENT: '#F6F7F9',
        MODAL_OVERLAY: transparentize('0.33', '#1A2952'),
    },

    // Dropdown
    DROPDOWN: {
        DROPDOWN_BORDER: '#D9DEE6',
        DROPDOWN_OPTION_HOVER: '#E1E5EC',
        DROPDOWN_OPTION_SELECTED: '#5897fb',
        DROPDOWN_OPTION_TEXT: '#04255B',
        DROPDOWN_DISABLED_OPTION_TEXT: '#CDD3DE',
        DROPDOWN_ARROW: '#d4d4d4',
        DROPDOWN_ARROW_DARK: '#647798',
    },

    // DATAGRID
    DATAGRID: {
        DATAGRID_TITLE_BG: '#F3F3F3',
        DATAGRID_HEADER_BG: '#E0EDF6',
        DATAGRID_TOTAL_BG: '#F6F9FB',
        DATAGRID_BUDGET_BG: '#FFF7DA',
        DATAGRID_DIVIDER_LINE: '#BABABA',
        DATAGRID_TEXT: '#2A3F5E',
    },

    LEGACY: {
        DATAGRID_BORDER: '#C7C7C7',
        DATAGRID_TH_BG: '#E0EDF6',
        DATAGRID_ARCHIVED_BG: '#FCF0F2',
    },
};

// fonts
export enum FONT_SIZE {
    XXX_LARGE = '48px',
    XX_LARGE = '36px',
    X_LARGE = '22px',
    LARGE = '18px',
    MEDIUM = '16px',
    SMALL = '14px',
    X_SMALL = '12px',
    XX_SMALL = '8px',
}

export const SPACING_SCALE = 8;

export enum SPACING {
    BUTTON_DEFAULT = '3px 12px',
    XXX_LARGE = '58px',
    XX_LARGE = '28px',
    X_LARGE = '22px',
    LARGE = '18px',
    MEDIUM = '12px',
    SMALL = '6px',
    X_SMALL = '4px',
    XX_SMALL = '2px',
}

export const PROXIMA_NOVA = '"Proxima Nova", Arial, sans-serif';
export const OPEN_SANS = 'open-sans, sans-serif';

export const FONT_STACK = `${OPEN_SANS}`;

export const FONT = {
    PROXIMA_NOVA,
    OPEN_SANS,
};

/**
 * Note: line-height is a very distinct concept from the font-size and won't map 1-to-1 with font-size.
 * It should also almost always be specified in unitless values.
 */
export enum LINE_HEIGHT {
    /**
     * line-height: 1.2; is a good standard line-height, it works well for inline text, buttons etc, should be expanded for larger sections of text
     */
    MEDIUM = 1.2,
    /**
     * line-height 1.4 lets the text breathe a bit more, which is good for paragraph text
     */
    LARGE = 1.4,
}

/**
 * Note: generally the margin-bottom of a paragraph should be 1em so it adjusts 1-to-1 with the font-size
 */
export enum TEXT_RELATIVE_MARGIN {
    XX_SMALL = '0.4em',
    X_SMALL = '0.6em',
    SMALL = '0.8em',
    MEDIUM = '1em',
    LARGE = '1.2em',
    X_LARGE = '1.4em',
    XX_LARGE = '1.6em',
}

export const FONT_SET = {
    XX_LARGE: `font-family: ${OPEN_SANS};
        font-size: ${FONT_SIZE.XX_LARGE};
        line-height: ${LINE_HEIGHT.MEDIUM};
    `,
    X_LARGE: `font-family: ${OPEN_SANS};
        font-size: ${FONT_SIZE.X_LARGE};
        line-height: ${LINE_HEIGHT.MEDIUM};
    `,
    LARGE: `font-family: ${OPEN_SANS};
        font-size: ${FONT_SIZE.LARGE};
        line-height: ${LINE_HEIGHT.MEDIUM};
    `,
    MEDIUM: `font-family: ${OPEN_SANS};
        font-size: ${FONT_SIZE.MEDIUM};
        line-height: ${LINE_HEIGHT.MEDIUM};
    `,
    SMALL: `font-family: ${OPEN_SANS};
        font-size: ${FONT_SIZE.SMALL};
        line-height: ${LINE_HEIGHT.MEDIUM};
    `,
    X_SMALL: `font-family: ${OPEN_SANS};
        font-size: ${FONT_SIZE.X_SMALL};
        line-height: ${LINE_HEIGHT.MEDIUM};
    `,
    XX_SMALL: `font-family: ${OPEN_SANS};
        font-size: ${FONT_SIZE.XX_SMALL};
        line-height: ${LINE_HEIGHT.MEDIUM};
    `,
};

export const PARAGRAPH = {
    DEFAULT: `color: ${COLOR.MAIN.PRIMARY};
    font-family: ${OPEN_SANS};
    font-size: ${FONT_SIZE.MEDIUM};
    line-height: ${LINE_HEIGHT.LARGE};
    margin-bottom: ${TEXT_RELATIVE_MARGIN.MEDIUM};
`,
    INDENT: `color: ${COLOR.MAIN.FORM_FIELD_DARK};
    font-family: ${OPEN_SANS};
    font-size: ${FONT_SIZE.MEDIUM};
    line-height: ${LINE_HEIGHT.LARGE};
    margin-bottom: ${TEXT_RELATIVE_MARGIN.MEDIUM};
    border-left: ${SPACING.MEDIUM} solid ${COLOR.MAIN.FORM_FIELD_DARK};
    padding-left: ${SPACING.MEDIUM};
`,
};

export const HEADER = {
    H1: `color: ${COLOR.MAIN.PRIMARY};
        font-family: ${OPEN_SANS};
        font-size: ${FONT_SIZE.X_LARGE};
        font-weight: 800;
        line-height: ${LINE_HEIGHT.LARGE};
        padding-bottom: ${SPACING.XX_LARGE};
        border-bottom: 1px solid ${COLOR.MAIN.PRIMARY};
        margin-bottom: ${TEXT_RELATIVE_MARGIN.MEDIUM};
    `,
    H2: `color: ${COLOR.MAIN.DARK_BLUE};
        font-family: ${OPEN_SANS};
        font-size: ${FONT_SIZE.LARGE};
        font-weight: 800;
        line-height: ${LINE_HEIGHT.LARGE};
        margin-bottom: ${TEXT_RELATIVE_MARGIN.MEDIUM};
    `,
    H3: `color: ${COLOR.MAIN.DARK_GREY};
        font-family: ${OPEN_SANS};
        font-size: ${FONT_SIZE.MEDIUM};
        font-weight: 800;
        line-height: ${LINE_HEIGHT.LARGE};
        margin-bottom: ${TEXT_RELATIVE_MARGIN.MEDIUM};
    `,
    H4: `color: ${COLOR.MAIN.LABEL_TEXT};
        font-family: ${OPEN_SANS};
        font-size: ${FONT_SIZE.SMALL};
        font-weight: 400;
        line-height: ${LINE_HEIGHT.LARGE};
        margin-bottom: ${TEXT_RELATIVE_MARGIN.MEDIUM};
    `,
    H5: `color: ${COLOR.MAIN.PRIMARY};
        font-family: ${OPEN_SANS};
        font-size: ${FONT_SIZE.X_SMALL};
        font-weight: 400;
        line-height: ${LINE_HEIGHT.LARGE};
        margin-bottom: ${TEXT_RELATIVE_MARGIN.MEDIUM};
    `,
    H6: `color: ${COLOR.MAIN.PRIMARY};
        font-family: ${OPEN_SANS};
        font-size: ${FONT_SIZE.SMALL};
        font-weight: 800;
        text-transform: uppercase;
        line-height: ${LINE_HEIGHT.LARGE};
        margin-bottom: ${TEXT_RELATIVE_MARGIN.MEDIUM};
    `,
};

export const BREAKPOINT = {
    MOBILE: '320px',
    TABLET: '641px',
    DESKTOP: '769px',
};

export const SPEED = {
    TRANSITION: '300ms',
    ROTATE: '600ms',
};

export const RADIUS = {
    FORM_FIELD: '3px',
    DEFAULT: '3px',
};

/** @deprecated use ModernScripts SHADOW instead */
export const SHADOW = {
    DEFAULT: '0 1px 2px 0 rgb(0 0 0 / 8%)',
};

export const ANIMATION = {
    FADE_IN: keyframes`
        0% {
            opacity: 0;
        }
        100% {
            opacity: 1;
        }
    `,
    FADE_OUT: keyframes`
        0% {
            opacity: 0;
        }
        100% {
            opacity: 1;
        }
    `,
    SCALE_IN: keyframes`
        0% {
            transform: scale(.1);
            opacity: 0;
        }
        100% {
            transform: scale(1);
            opacity: 1;
        }
    `,
    ROTATE_CLOCKWISE: keyframes`
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    `,
};

export const BUTTON_MIN_WIDTH = {
    SMALL: '150px',
};
